/** @file Minimal hash router. */
import { confirmDiscardDirtyEditor } from "./editor-dirty.js";

/** @typedef {"editor" | "login" | "signup" | "reset" | "plays" | "teams" | "account" | "help"} Route */

const ROUTES = new Set(["editor", "login", "signup", "reset", "plays", "teams", "account", "help"]);

/** @returns {Route} */
export function getRoute() {
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const raw = hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  if (raw === "" || raw === "/") {
    return "editor";
  }
  return /** @type {Route} */ (ROUTES.has(raw) ? raw : "editor");
}

/**
 * @param {Route} route
 */
export function navigate(route) {
  if (typeof window === "undefined") {
    return false;
  }
  const current = getRoute();
  if (current === "editor" && route !== "editor" && !confirmDiscardDirtyEditor()) {
    return false;
  }
  const next = route === "editor" ? "#/" : `#/${route}`;
  if (window.location.hash !== next) {
    window.location.hash = next;
  }
  return true;
}

/**
 * @param {(route: Route) => void} fn
 * @returns {() => void}
 */
export function subscribeRoute(fn) {
  if (typeof window === "undefined") {
    return () => {};
  }
  let previous = getRoute();
  let reverting = false;
  const handler = () => {
    const next = getRoute();
    if (!reverting && previous === "editor" && next !== "editor" && !confirmDiscardDirtyEditor()) {
      reverting = true;
      window.location.hash = previous === "editor" ? "#/" : `#/${previous}`;
      reverting = false;
      return;
    }
    previous = next;
    fn(next);
  };
  window.addEventListener("hashchange", handler);
  return () => window.removeEventListener("hashchange", handler);
}

/**
 * Returns true if the route requires an authenticated user.
 * @param {Route} route
 * @returns {boolean}
 */
export function requireAuth(route) {
  return route === "editor" || route === "plays" || route === "teams" || route === "account";
}

/**
 * Auth-only routes (login/signup/reset) redirect signed-in users to editor.
 * @param {Route} route
 * @returns {boolean}
 */
export function isAuthOnlyRoute(route) {
  return route === "login" || route === "signup" || route === "reset";
}
